import React, { useState, useRef } from 'react';
import { db } from '../firebase'; 
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { 
  ShieldAlert, 
  CheckCircle2, 
  SendHorizontal, 
  Users2, 
  Award, 
  MapPin, 
  FileText, 
  Binary,
  UploadCloud,
  X,
  FileImage
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const SubmitAchievement = () => {
  // --- Form States ---
  const [studentNames, setStudentNames] = useState([]);
  const [currentNameInput, setCurrentNameInput] = useState('');
  const [formData, setFormData] = useState({
    achievement: '',
    destination: '',
    description: '',
    schoolCode: ''
  });
  
  // --- Base64 Image Processing States ---
  const [base64ImageString, setBase64ImageString] = useState(null);
  const [imageMeta, setImageMeta] = useState({ name: '', size: 0 });
  
  // --- UI Control States ---
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const nameInputRef = useRef(null);

  // --- Helper to count words ---
  const getWordCount = (text) => {
    return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
  };

  // --- Handle Description Input with a strict 35 Word Limit ---
  const handleDescriptionChange = (e) => {
    const text = e.target.value;
    if (getWordCount(text) <= 35) {
      setFormData({ ...formData, description: text });
    }
  };

  // --- Mobile & Desktop Friendly Name Tag Creator ---
  const processNamesText = (textValue) => {
    if (textValue.includes(',')) {
      const parts = textValue.split(',');
      const completedName = parts[0].trim();
      
      if (completedName && !studentNames.includes(completedName)) {
        setStudentNames([...studentNames, completedName]);
      }
      setCurrentNameInput(parts[1] || '');
    } else {
      setCurrentNameInput(textValue);
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedValue = currentNameInput.trim();
      if (trimmedValue && !studentNames.includes(trimmedValue)) {
        setStudentNames([...studentNames, trimmedValue]);
        setCurrentNameInput('');
      }
    }
    
    if (e.key === 'Backspace' && currentNameInput === '' && studentNames.length > 0) {
      e.preventDefault();
      const updatedNames = [...studentNames];
      const lastTag = updatedNames.pop();
      setStudentNames(updatedNames);
      setCurrentNameInput(lastTag + ' ');
    }
  };

  const handleNameBlur = () => {
    setFocusedField(null);
    const trimmedValue = currentNameInput.trim();
    if (trimmedValue && !studentNames.includes(trimmedValue)) {
      setStudentNames([...studentNames, trimmedValue]);
      setCurrentNameInput('');
    }
  };

  const removeNameTag = (indexToRemove) => {
    setStudentNames(studentNames.filter((_, index) => index !== indexToRemove));
  };

  // --- Image Processing & Base64 Generation ---
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setStatus({ type: 'error', msg: 'Please upload an image file (PNG or JPG).' });
      return;
    }

    if (file.size > 800 * 1024) {
      setStatus({ 
        type: 'error', 
        msg: 'Image size is too large! Please upload a screenshot or a compressed photo under 800KB.' 
      });
      return;
    }

    setImageMeta({ name: file.name, size: file.size });

    const reader = new FileReader();
    reader.onloadend = () => {
      setBase64ImageString(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // --- Form Submission Handler ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', msg: '' });

    let finalNamesList = [...studentNames];
    if (currentNameInput.trim()) {
      finalNamesList.push(currentNameInput.trim());
    }

    if (finalNamesList.length === 0) {
      setStatus({ type: 'error', msg: 'Please add at least one student name.' });
      return;
    }

    const codeRegex = /^\d{7}$/;
    if (!codeRegex.test(formData.schoolCode.trim())) {
      setStatus({ type: 'error', msg: 'The Student Code must be exactly 7 numbers.' });
      return;
    }

    if (!base64ImageString) {
      setStatus({ type: 'error', msg: 'Please upload an image proof to complete submission.' });
      return;
    }

    setIsSubmitting(true);
    const compiledNames = finalNamesList.join(' & ');

    try {
      // 1. إضافة الطلب إلى قاعدة بيانات Firestore
      await addDoc(collection(db, "achievements"), {
        student: compiledNames,
        achievement: formData.achievement,
        destination: formData.destination,
        description: formData.description,
        schoolCode: formData.schoolCode.trim(),
        imageUrl: base64ImageString,
        status: "pending", 
        createdAt: serverTimestamp()
      });

      // 2. إرسال الإشعار عبر EmailJS
      const templateParams = {
        achievement_title: formData.achievement,
        school_code: formData.schoolCode.trim(),
        student_names: compiledNames,
        destination: formData.destination,
        description: formData.description,
      };

      await emailjs.send(
        'service_4xv7bkj',   // Service ID الخاص بك
        'template_12eavvl',  // استبدله بـ Template ID من حساب EmailJS
        templateParams,
        'P7AjRQUHFBJ9WrjRF'    // استبدله بـ Public Key الخاص بك
      );

      setStatus({ 
        type: 'success', 
        msg: 'Success! Your achievement is submitted and waiting for review.' 
      });
      
      // Reset Form State
      setStudentNames([]);
      setCurrentNameInput('');
      setBase64ImageString(null);
      setImageMeta({ name: '', size: 0 });
      setFormData({ achievement: '', destination: '', description: '', schoolCode: '' });
    } catch (err) {
      console.error("Pipeline breakdown details:", err);
      setStatus({ type: 'error', msg: 'Submission failed. Please check your network and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full bg-[#fafafa] text-slate-900 font-sans antialiased flex flex-col justify-center items-center py-12 px-4 md:px-6">
      <Helmet>
        <title>Submit Student Achievement | Registry Portal</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* --- Main Workspace Architecture --- */}
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Panel */}
        <div className="lg:col-span-4 bg-white border border-slate-200/80 rounded-2xl p-6 space-y-6 text-left shadow-sm">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 uppercase">Submission Registry</h1>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Verification Gateway</p>
          </div>

          <hr className="border-slate-100" />

          <div className="space-y-4">
            <div className="space-y-1">
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">1. Roster Input</h2>
              <p className="text-xs text-slate-500 font-medium leading-relaxed">
                Type names separated by commas. Each entry converts automatically into an isolated tag module.
              </p>
            </div>

            <div className="space-y-1">
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">2. Documented Proof</h2>
              <p className="text-xs text-slate-500 font-medium leading-relaxed">
                Attach an official digital screenshot, document image, or certificate file. This field is mandatory.
              </p>
            </div>

            <div className="space-y-1">
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">3. Authorization</h2>
              <p className="text-xs text-slate-500 font-medium leading-relaxed">
                Sign off the verified achievement by appending your standard 7-digit institutional student key.
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="lg:col-span-8 bg-white border border-slate-200/80 rounded-2xl shadow-xl shadow-slate-100/40 p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Input Section: Name Tags System */}
            <div className="space-y-2 text-left">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <Users2 size={14} className="text-slate-400" /> Student Names
              </label>
              
              <div 
                onClick={() => !isSubmitting && nameInputRef.current?.focus()}
                className={`w-full min-h-[52px] p-2 bg-slate-50/50 border rounded-xl flex flex-wrap gap-2 transition-all duration-150 cursor-text ${
                  focusedField === 'names' ? 'border-slate-900 bg-white shadow-xs' : 'border-slate-200'
                } ${isSubmitting ? 'opacity-60 pointer-events-none' : ''}`}
              >
                {studentNames.map((name, index) => (
                  <span 
                    key={index} 
                    className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 bg-slate-900 text-white text-xs font-semibold rounded-lg border border-slate-800"
                  >
                    {name}
                    <button 
                      type="button" 
                      disabled={isSubmitting}
                      onClick={(e) => { e.stopPropagation(); removeNameTag(index); }}
                      className="p-0.5 hover:bg-white/20 rounded-md text-white/70 hover:text-white transition-colors disabled:opacity-30"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                
                <input
                  ref={nameInputRef}
                  type="text"
                  disabled={isSubmitting}
                  placeholder={studentNames.length === 0 ? "e.g. Alice Smith, Bob Jones" : "Add next..."}
                  value={currentNameInput}
                  onChange={(e) => processNamesText(e.target.value)}
                  onKeyDown={handleNameKeyDown}
                  onFocus={() => setFocusedField('names')}
                  onBlur={handleNameBlur}
                  className="flex-grow min-w-[140px] bg-transparent outline-none border-none py-1 px-1 text-sm font-semibold text-slate-900 placeholder:text-slate-400 disabled:opacity-50"
                />
              </div>
            </div>

            {/* Input Section: Achievement Meta Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <Award size={14} className="text-slate-400" /> Achievement Title
                </label>
                <input 
                  type="text" required placeholder="e.g. 1st Place Winner" 
                  disabled={isSubmitting}
                  onFocus={() => setFocusedField('achievement')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3.5 bg-slate-50/50 border rounded-xl text-sm font-semibold transition-all placeholder:text-slate-400 text-slate-900 outline-none disabled:opacity-50 ${
                    focusedField === 'achievement' ? 'border-slate-900 bg-white shadow-xs' : 'border-slate-200'
                  }`}
                  value={formData.achievement} 
                  onChange={e => setFormData({...formData, achievement: e.target.value})} 
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <MapPin size={14} className="text-slate-400" /> Hosting Competition / Arena
                </label>
                <input 
                  type="text" required placeholder="e.g. Nile University" 
                  disabled={isSubmitting}
                  onFocus={() => setFocusedField('destination')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3.5 bg-slate-50/50 border rounded-xl text-sm font-semibold transition-all placeholder:text-slate-400 text-slate-900 outline-none disabled:opacity-50 ${
                    focusedField === 'destination' ? 'border-slate-900 bg-white shadow-xs' : 'border-slate-200'
                  }`}
                  value={formData.destination} 
                  onChange={e => setFormData({...formData, destination: e.target.value})} 
                />
              </div>
            </div>

            {/* Input Section: Content Description Segment */}
            <div className="space-y-2 text-left">
              <div className="flex justify-between items-center">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <FileText size={14} className="text-slate-400" /> Brief Description
                </label>
                <span className={`text-[11px] font-bold ${getWordCount(formData.description) >= 35 ? 'text-red-500' : 'text-slate-400'}`}>
                  {getWordCount(formData.description)} / 35 words
                </span>
              </div>
              <textarea 
                required rows="4" 
                disabled={isSubmitting}
                placeholder="Describe your project system details or award milestones..." 
                onFocus={() => setFocusedField('desc')}
                onBlur={() => setFocusedField(null)}
                className={`w-full px-4 py-3.5 bg-slate-50/50 border rounded-xl text-sm font-semibold transition-all placeholder:text-slate-400 text-slate-900 outline-none resize-none leading-relaxed disabled:opacity-50 ${
                  focusedField === 'desc' ? 'border-slate-900 bg-white shadow-xs' : 'border-slate-200'
                }`}
                value={formData.description} 
                onChange={handleDescriptionChange}
              />
            </div>

            {/* Input Section: Mandatory File Dropzone */}
            <div className="space-y-2 text-left">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <FileImage size={14} className="text-slate-400" /> Upload Image Proof <span className="text-red-500">*</span>
              </label>
              
              {!base64ImageString ? (
                <label className={`w-full min-h-[130px] border-2 border-dashed border-slate-200 hover:border-slate-900 bg-slate-50/50 hover:bg-white rounded-xl flex flex-col items-center justify-center p-6 transition-all duration-150 cursor-pointer group ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                  <input type="file" required accept="image/*" disabled={isSubmitting} className="hidden" onChange={handleImageChange} />
                  <UploadCloud size={24} className="text-slate-400 group-hover:text-slate-900 transition-colors mb-1" />
                  <p className="text-xs font-bold text-slate-700">Click to attach certificate photo</p>
                  <p className="text-[10px] text-slate-400 font-medium mt-0.5">PNG or JPG formats (Max 800KB)</p>
                </label>
              ) : (
                <div className="w-full border border-slate-200 rounded-xl p-3 bg-slate-50/50 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 overflow-hidden">
                    <img src={base64ImageString} alt="Thumbnail Preview" className="w-12 h-12 object-cover rounded-lg border border-slate-200 shrink-0" />
                    <div className="overflow-hidden">
                      <p className="text-xs font-bold text-slate-900 truncate">{imageMeta.name}</p>
                      <p className="text-[10px] text-slate-400 font-medium">{(imageMeta.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    disabled={isSubmitting}
                    onClick={() => { setBase64ImageString(null); setImageMeta({ name: '', size: 0 }); }}
                    className="p-2 hover:bg-rose-50 text-slate-400 hover:text-rose-600 border border-slate-200/60 hover:border-rose-200 rounded-xl transition-all disabled:opacity-40"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* Input Section: Identity Code Verification & Commit Dispatch */}
            <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row gap-6 items-end justify-between text-left">
              <div className="w-full sm:max-w-xs space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <Binary size={14} className="text-slate-400" /> Student Code
                </label>
                <input 
                  type="text" required maxLength="7" placeholder="7-digit ID number" 
                  disabled={isSubmitting}
                  onFocus={() => setFocusedField('code')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3.5 bg-slate-50/50 border rounded-xl text-sm font-semibold transition-all text-slate-900 outline-none disabled:opacity-50 ${
                    focusedField === 'code' ? 'border-slate-900 bg-white shadow-xs' : 'border-slate-200'
                  }`}
                  value={formData.schoolCode} 
                  onChange={e => setFormData({...formData, schoolCode: e.target.value.replace(/\D/g, '')})} 
                />
              </div>
              
              <button 
                type="submit" disabled={isSubmitting}
                className="w-full sm:w-auto px-8 h-[48px] bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer active:scale-[0.98]"
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing File & Syncing...
                  </div>
                ) : (
                  <>
                    Submit Achievement <SendHorizontal size={13} />
                  </>
                )}
              </button>
            </div>

            {/* Feedback Notifications Feed */} 
            <AnimatePresence mode="wait">
              {status.msg && (
                <motion.div 
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  className={`p-4 rounded-xl text-xs font-bold border flex items-start gap-3 ${
                    status.type === 'success' ? 'bg-emerald-50 text-emerald-900 border-emerald-200' : 'bg-rose-50 text-rose-900 border-rose-200'
                  }`}
                >
                  {status.type === 'success' ? (
                    <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                  ) : (
                    <ShieldAlert size={16} className="text-rose-600 shrink-0 mt-0.5" />
                  )}
                  <span>{status.msg}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>

      </div>
    </div>
  );
};

export default SubmitAchievement;